import classNames from 'classnames';
import React from 'react';

import Cancelcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

import { translate } from '@vestahealthcare/common/i18n';
import {
  CareTeamMember,
  CareTeamMemberType,
  UserPhoneVerified,
} from '@vestahealthcare/common/models';

import { Chip, Colors, PhoneLink, Tooltip } from 'styleguide-v2';

import { CareTeamMemberPermissions } from 'dash/src/pages/CareTeam/components/CareTeamMemberPermissions';

import { RowStyles } from './ContactInfoRowStyles';

type Props = {
  className?: string;
  ctm: CareTeamMember;
  editMode?: boolean;
  index?: number;
};

const getVerifiedIcon = (
  type: CareTeamMemberType,
  phone: UserPhoneVerified,
) => {
  if (type?.id === CareTeamMemberType.PROVIDER) {
    return (
      <Tooltip
        text={translate(
          `careTeam.common.${
            phone?.verifiedAt ? 'tooltipVerified' : 'tooltipNotVerified'
          }`,
        )}
      >
        {phone?.verifiedAt ? (
          <CheckIcon htmlColor={Colors.iconGreen} />
        ) : (
          <Cancelcon color="error" />
        )}
      </Tooltip>
    );
  }
};

export const ContactInfoCTMRow = ({
  className,
  ctm,
  editMode,
  index = 0,
}: Props) => {
  const styles = RowStyles();

  const {
    preferred,
    careTeamPerson: { id, fullName, phones, languages },
    type,
    typeWithSubType,
  } = ctm;

  return (
    <div
      className={classNames(
        className,
        !editMode && index % 2 === 1 && styles.rowEven,
        styles.row,
      )}
    >
      <div
        className={classNames(
          className,
          'grid-wrapper rows-24',
          styles.rowInner,
        )}
      >
        <span
          className={classNames(
            'grid-span-4 flex min-gap',
            editMode && preferred && styles.preferredIcon,
          )}
        >
          {preferred && (
            <Tooltip text="Preferred">
              <StarIcon color="primary" />
            </Tooltip>
          )}
          {fullName}
        </span>
        <div className="grid-span-6 flex gap wrap">
          {!!typeWithSubType.type && (
            <Chip color="primary" type="outlined" size="small">
              {typeWithSubType.type?.description}
            </Chip>
          )}
          {!!typeWithSubType.subtype && (
            <Chip color="secondary" type="outlined" size="small">
              {typeWithSubType.subtype}
            </Chip>
          )}
        </div>
        <div className="grid-span-7 flex gap wrap">
          {!editMode && <CareTeamMemberPermissions member={ctm} />}
        </div>
        <div className="grid-span-2 flex gap wrap end">
          {languages?.map((item) => (
            <Chip
              color="info"
              key={`member-language-${item.language.value}`}
              type={item.preferred ? 'contained' : 'outlined'}
              size="small"
            >
              {item.language.toString()}
            </Chip>
          ))}
        </div>
        <div className="grid-span-5">
          {phones?.[0]?.phone?.number && (
            <PhoneLink
              compact
              icon={
                <div className="flex gap">
                  {getVerifiedIcon(type, phones[0])}
                  {phones[0].primary && (
                    <span className="size-xs grey">
                      ({translate('global.primary')})
                    </span>
                  )}
                </div>
              }
              phone={phones[0].phone.number}
              type={phones[0].phone.type?.toString()}
            />
          )}
        </div>
      </div>
      {!editMode &&
        phones.length > 1 &&
        phones?.slice(1)?.map((otherPhone) => (
          <div
            key={`contact-caregiver-${id}-phone-${otherPhone.phone.number}`}
            className={classNames('grid-wrapper rows-24', styles.rowInner)}
          >
            <div className="grid-span-19" />
            <div className="grid-span-5">
              {otherPhone && (
                <PhoneLink
                  compact
                  icon={
                    <div className="flex gap">
                      {getVerifiedIcon(type, otherPhone)}
                      {otherPhone.primary && (
                        <span className="size-xs grey">
                          ({translate('global.primary')})
                        </span>
                      )}
                    </div>
                  }
                  phone={otherPhone.phone.number}
                  type={otherPhone.phone.type?.toString()}
                />
              )}
            </div>
          </div>
        ))}
    </div>
  );
};
